import * as React from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import HelpIcon from '@mui/icons-material/Help';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    SETTINGS_LOCATION,
    LOCATION_MAPPING,
    SUPPORT,
    BUDGET_USER,
    ADMIN,
    DECISION_USER,
} from 'business/constants';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGetNotifications } from 'components/Notifications/hooks';
import { Badge, BadgeProps, styled } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { UserRole } from 'business/definitions';
import { intersection } from 'lodash';
import { NotificationGrid } from 'components/Notifications/NotificationGrid';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { TRACKABLE_EVENTS, trackEvent } from 'helpers/trackerHelpers';
import HoverPopper from './HoverPopper';
import { SettingsMenu } from './ui/SettingsMenu';
import { toggleHubspotChat } from './helpers';
import MenuSideButton from './ui/MenuSideButton';
import { SideBarMode } from './hooks';
import { ClickPopover } from './ClickPopover';

type Props = {
    client: ClientT;
    setShowAlert: DispatchState<boolean>;
    isAuthenticated: boolean;
    logout: () => void;
    isAdmin: boolean;
    sideBarMode: SideBarMode;
    isExpanded: boolean;
    setExpanded: DispatchState<boolean>;
    userRoles: UserRole[];
};

const StyledBadge = styled(Badge, {
    name: 'StyledBadge',
})<BadgeProps>({
    '& .MuiBadge-badge': {
        width: '1em',
        height: '1em',
        right: '0.3em',
        top: '0.3em',
        minWidth: '0px',
    },
});

export default function SideBarFooterItems({
    client,
    setShowAlert,
    isAuthenticated,
    logout,
    isAdmin,
    sideBarMode,
    isExpanded,
    setExpanded,
    userRoles,
}: Props) {
    const { t } = useTranslation();
    const location = useLocation();
    const queryClient = useQueryClient();
    const subLocation = location.pathname.split('/')[1];

    const openSupport = () => {
        if (client.owner !== 'manty') {
            setShowAlert(true);
        } else {
            toggleHubspotChat();
        }
    };

    // NOTIFICATIONS
    const showNotificationPanel =
        intersection(userRoles, [SUPPORT, BUDGET_USER, ADMIN, DECISION_USER])
            .length > 0;
    const { data: notifications, isRefetching } = useGetNotifications(
        showNotificationPanel,
    );

    useEffect(() => {
        if (isRefetching) {
            queryClient.invalidateQueries({
                queryKey: ['unread_notifications'],
            });
        }
    }, [isRefetching]);

    const isUnreadNotification = notifications?.some((e) => !e.read);
    const notifIcon = isUnreadNotification ? (
        <StyledBadge badgeContent="" color="error">
            <NotificationsIcon />
        </StyledBadge>
    ) : (
        <NotificationsIcon />
    );

    return (
        <Grid
            container
            direction="column"
            justifyContent="flex-end"
            sx={{ flexGrow: 1 }}
        >
            {isAuthenticated && (
                <>
                    {showNotificationPanel && (
                        <ClickPopover
                            component={
                                <MenuSideButton
                                    sideBarMode={sideBarMode}
                                    icon={notifIcon}
                                    text={t('modules.notification')}
                                    isSideBarExpanded={isExpanded}
                                    setExpanded={setExpanded}
                                    isAvailable
                                />
                            }
                            onClick={() =>
                                trackEvent(
                                    TRACKABLE_EVENTS.global.ux
                                        .notificationsCenter.clickIcon,
                                )
                            }
                            renderChildren={(closePopover: () => void) => (
                                <NotificationGrid
                                    notifications={notifications}
                                    isUnreadNotification={isUnreadNotification}
                                    onRowClick={closePopover}
                                />
                            )}
                        />
                    )}
                    <MenuSideButton
                        sideBarMode={sideBarMode}
                        isSideBarExpanded={isExpanded}
                        setExpanded={setExpanded}
                        key="help"
                        // We use this className to anchor the Resource Center here from Pendo
                        className="pendo-resource-center"
                        text={t('header.resourceCenter')}
                        icon={<HelpIcon />}
                    />
                    <MenuSideButton
                        sideBarMode={sideBarMode}
                        isSideBarExpanded={isExpanded}
                        setExpanded={setExpanded}
                        key="support"
                        text={t('header.support')}
                        icon={<ChatBubbleIcon />}
                        onClick={openSupport}
                    />
                    <HoverPopper
                        key="settings"
                        component={
                            <MenuSideButton
                                sideBarMode={sideBarMode}
                                isSideBarExpanded={isExpanded}
                                setExpanded={setExpanded}
                                text={t('header.settings')}
                                icon={<SettingsIcon />}
                                className="e2e-header-profile-button"
                                isSelected={
                                    LOCATION_MAPPING[subLocation] ===
                                    SETTINGS_LOCATION
                                }
                                showTooltip={false}
                            />
                        }
                        popperProps={{
                            open: false,
                            placement: 'right-start',
                        }}
                    >
                        <SettingsMenu logout={logout} isAdmin={isAdmin} />
                    </HoverPopper>
                </>
            )}
        </Grid>
    );
}
